"use client";
/**
 * The browser's half of the Real / Paper switch (lib/wealth/accountMode.ts).
 *
 * The choice is one per-device key. While it says Paper:
 *
 *   - every request to an account route (`/api/wealth`, `/api/trades`,
 *     `/api/ibkr`) carries the mode header. That is done in ONE place, a wrap
 *     of `window.fetch` installed before the app renders (src/client/main.tsx),
 *     so no caller has to remember it and a fetch added next month is covered;
 *   - an answer to such a request that does not say it was made in Paper mode
 *     is thrown away and replaced by a 409 with a sentence. A server that does
 *     not know the header (an older build, a proxy that dropped it) would
 *     answer with real data: this is what keeps it off the screen;
 *   - browser keys that hold account data are read and written under a paper
 *     twin (lib/safeLocalStorage.ts);
 *   - a link to an account route is not followed: a navigation cannot carry
 *     the header, so it would be answered in Real mode. (The two broker
 *     sign-in links are plain buttons in Paper, with the reason in words.)
 *
 * Changing the mode reloads the page: whatever a component still holds in
 * memory from the other mode goes with it.
 */
import { ACCOUNT_MODE_HEADER, ACCOUNT_MODE_KEY, isAccountApiPath, type AccountMode } from "./accountMode";
import { safeLocalStorageSet } from "@/lib/safeLocalStorage";

/** This device's mode. Real when storage cannot be read. */
export function clientAccountMode(): AccountMode {
  try {
    return typeof window !== "undefined" && window.localStorage.getItem(ACCOUNT_MODE_KEY) === "paper" ? "paper" : "real";
  } catch {
    return "real";
  }
}

export function isClientPaperMode(): boolean {
  return clientAccountMode() === "paper";
}

/** Store the choice (per device, never synced). The caller reloads. */
export function storeClientAccountMode(mode: AccountMode): boolean {
  return safeLocalStorageSet(ACCOUNT_MODE_KEY, mode);
}

function pathOf(input: RequestInfo | URL): string | null {
  try {
    const raw = typeof input === "string" ? input : input instanceof URL ? input.href : input.url;
    return new URL(raw, window.location.origin).pathname;
  } catch {
    return null;
  }
}

const NOT_PAPER =
  "Paper account: the server did not answer this request in Paper mode, so its answer is not shown. Reload the page; if it stays, switch to Real.";

let installed = false;

/** Wrap `window.fetch` once. Does nothing to a request while the mode is Real. */
export function installAccountModeFetch(): void {
  if (installed || typeof window === "undefined" || typeof window.fetch !== "function") return;
  installed = true;
  const original = window.fetch.bind(window);
  window.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    if (!isClientPaperMode()) return original(input, init);
    const path = pathOf(input);
    if (path == null || !isAccountApiPath(path)) return original(input, init);
    const headers = new Headers(init?.headers ?? (typeof input !== "string" && !(input instanceof URL) ? input.headers : undefined));
    headers.set(ACCOUNT_MODE_HEADER, "paper");
    // Never from the browser's own cache: a Real answer kept there for a
    // minute (the routes that allow it) would be handed back without asking.
    const res = await original(input, { ...init, headers, cache: "no-store" });
    if (res.ok && res.headers.get(ACCOUNT_MODE_HEADER) !== "paper") {
      return new Response(JSON.stringify({ error: NOT_PAPER, paper: true }), {
        status: 409,
        headers: { "content-type": "application/json", "cache-control": "no-store" },
      });
    }
    return res;
  };
  // A link is a navigation, and a navigation cannot carry the header: it would
  // be answered in Real mode. No page links to an account route in Paper (the
  // broker sign-in links are buttons there), and this stops one that does.
  document.addEventListener(
    "click",
    (event) => {
      if (!isClientPaperMode()) return;
      const link = (event.target as Element | null)?.closest?.("a[href]") as HTMLAnchorElement | null;
      if (!link) return;
      const path = pathOf(link.href);
      if (path != null && isAccountApiPath(path)) event.preventDefault();
    },
    true,
  );
}

export type PaperDataStatus = {
  ready: boolean;
  pending?: boolean;
  accounts: number;
  books: number;
  transactions: number;
  trades: number;
  through: string | null;
};

/**
 * Ask the server to make sure Paper's household exists and reaches today, or
 * to generate it again (`reset`), and wait for it: a first fill answers
 * `pending` until it is written.
 */
export async function requestPaperData(action: "ensure" | "reset", signal?: { cancelled: boolean }): Promise<PaperDataStatus> {
  for (let attempt = 0; attempt < 40; attempt += 1) {
    const res = await fetch("/api/wealth/paper", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      // A reset is asked once; the polls that follow only wait for it.
      body: JSON.stringify({ action: attempt === 0 ? action : "ensure" }),
    });
    const body = (await res.json().catch(() => null)) as (PaperDataStatus & { error?: string; retryInMs?: number }) | null;
    if (!res.ok || !body) throw new Error(body?.error ?? `Paper data request failed (${res.status})`);
    if (!body.pending) return body;
    if (signal?.cancelled) return body;
    await new Promise((resolve) => window.setTimeout(resolve, body.retryInMs ?? 800));
  }
  throw new Error("The paper data is still being written. Try again in a moment.");
}
